import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponseEnvelope } from '@doctor-scheduling/shared/models';
import { API_BASE_URL } from './api-base-url.token';

/** Time-series point for dashboard charts. */
export interface DashboardChartPoint {
  label: string;
  value: number;
}

/** Categorical slice for dashboard charts. */
export interface DashboardChartSlice {
  label: string;
  value: number;
}

/** Aggregated dashboard metrics, charts, and recent activity. */
export interface DashboardData {
  stats: {
    totalAppointments: number;
    activePatients: number;
    activeDoctors: number;
    pendingAppointments: number;
    completedAppointments: number;
    cancelledAppointments: number;
    todaysAppointments: number;
    confirmedAppointments: number;
  };
  charts: {
    appointmentsByDay: DashboardChartPoint[];
    patientsRegisteredByDay: DashboardChartPoint[];
    appointmentsByStatus: DashboardChartSlice[];
    doctorWorkload: DashboardChartSlice[];
  };
  upcomingAppointments: {
    id: string;
    patientName: string;
    doctorName: string;
    specialization: string;
    date: string;
    startTime: string;
    endTime: string;
    status: string;
    type: string;
  }[];
  recentPatients: {
    id: string;
    name: string;
    email: string | null;
    phone: string;
    createdAt: string;
  }[];
  recentActivity: {
    id: string;
    /** Patient display name. */
    patientName: string;
    /** Doctor display name (includes Dr. prefix). */
    doctorName: string;
    summary: string;
    /** Activity event type (mirrors appointment status). */
    type: string;
    /** Human-readable description for the activity type. */
    description: string;
    status: string;
    updatedAt: string;
  }[];
}

/** HTTP client for the clinic dashboard summary endpoint. */
@Injectable({
  providedIn: 'root',
})
export class DashboardApi {
  /** Angular HTTP client used for API requests. */
  private readonly http = inject(HttpClient);
  /** NestJS API origin from the web app environment. */
  private readonly apiBaseUrl = inject(API_BASE_URL);
  /** Base URL for dashboard routes under `/api/v1/dashboard`. */
  private readonly baseUrl = `${this.apiBaseUrl}/api/v1/dashboard`;

  /**
   * Loads dashboard stats, charts, upcoming appointments, and recent patients.
   * @returns Observable of the dashboard data envelope
   */
  getDashboardData(): Observable<ApiResponseEnvelope<DashboardData>> {
    return this.http.get<ApiResponseEnvelope<DashboardData>>(this.baseUrl);
  }
}
